// useDashboard — the single data seam for the dashboard (W36.0).
//
// Spec §8 makes this the reason Phase 1 and Phase 2 could be split without
// rework: "資料讀取**全部收進 `useDashboard()` 這一層**" and "換成聚合端點,
// **組件不動**". Components read from here and never call the API directly, so
// swapping the data source is a change in this file alone.
//
// What this hook deliberately does NOT do:
//
// - It does not fan out into per-module queries. The first paint waits on one
//   request (spec §5.1).
// - It does not swallow a failing module. `payload.modules[].error` carries it
//   through so the row can degrade on its own (spec §3.4).
// - It does not turn "not paired" into an empty page. That surfaces as
//   `noPair` so the route can render the pairing prompt *only*.

import { useCallback } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";

import {
  deleteDailyMessage,
  fetchDashboard,
  isNoPairError,
  listDailyMessages,
  listNotifications,
  markAllNotificationsRead,
  markDailyMessageRead,
  markNotificationRead,
  putDailyMessage,
  putMoodCheckIn,
  type DailyMessageArchive,
  type DashboardPayload,
  type NotificationList,
} from "@/lib/api/dashboard";
import { queryKeys } from "@/lib/queries/keys";

export interface UseDashboardResult {
  data: DashboardPayload | undefined;
  isLoading: boolean;
  isError: boolean;
  /** 409 DASHBOARD_NO_PAIR — render the pairing prompt and nothing else. */
  noPair: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
}

// The dashboard is a first paint, not a live feed: 30s keeps the badge and the
// daily message reasonably fresh without polling while someone is reading.
const DASHBOARD_STALE_TIME_MS = 30_000;

export function useDashboard(
  /**
   * W47.1: the coordinates the weather block is keyed on.
   *
   * They are part of the **query key**, not just the request, because a cached
   * Hong Kong reading is a different answer from a cached Vancouver one — a
   * stable key would let the fallback survive a grant and show the wrong city for
   * the rest of the session. `{null, null}` is the initial value, so the first
   * paint asks for Hong Kong and refetches once a fix arrives.
   */
  coords: { lat: number | null; lon: number | null } = { lat: null, lon: null },
  options?: Partial<UseQueryOptions<DashboardPayload, Error>>,
): UseDashboardResult {
  const query = useQuery<DashboardPayload, Error>({
    queryKey: [...queryKeys.dashboard.today(), coords.lat, coords.lon],
    queryFn: () => fetchDashboard(coords),
    staleTime: DASHBOARD_STALE_TIME_MS,
    retry: false,
    ...options,
  });

  const noPair = query.isError && isNoPairError(query.error);

  const refetch = useCallback(async () => {
    await query.refetch();
  }, [query]);

  return {
    data: query.data,
    isLoading: query.isLoading,
    // A "no pair" answer is not a page-level failure — it is a state.
    isError: query.isError && !noPair,
    noPair,
    error: query.isError && !noPair ? query.error : null,
    refetch,
  };
}

/** Review archive — cursor paginated, newest first (spec §5.2). */
export function useDailyMessageArchive(enabled = true) {
  return useQuery<DailyMessageArchive, Error>({
    queryKey: queryKeys.dashboard.messages(),
    queryFn: () => listDailyMessages(null, 30),
    enabled,
    staleTime: DASHBOARD_STALE_TIME_MS,
    retry: false,
  });
}

/** Inbox for the header bell sheet (spec §5.4). */
export function useNotifications(enabled = true) {
  return useQuery<NotificationList, Error>({
    queryKey: queryKeys.dashboard.notifications(),
    queryFn: () => listNotifications(false, 20),
    enabled,
    staleTime: 15_000,
    retry: false,
  });
}

/**
 * Write today's message.
 *
 * Invalidates the dashboard because the message appears in three places at
 * once (hero block, module row, badge) and they must not disagree — the same
 * reasoning as the backend deriving all three from one row.
 */
export function useSaveDailyMessage(date: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (content: string) => putDailyMessage(date, content),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
}

export function useClearDailyMessage(date: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => deleteDailyMessage(date),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
}

export function useMarkDailyMessageRead(date: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => markDailyMessageRead(date),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
}

export function useSaveMoodCheckIn(date: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (moodCode: string) => putMoodCheckIn(date, moodCode),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => markNotificationRead(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
}

/**
 * Clear the whole badge in one request (ADR-0069).
 *
 * The alternative the loop of `useMarkNotificationRead` calls would have been
 * is 20 racing requests whose interleaving decides what the badge ends up
 * saying. One request, one truth.
 */
export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => markAllNotificationsRead(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
}
